// src/components/NotificacionesComponent.jsx
import React, { useState, useEffect } from "react";
import "./NotificacionesComponent.css";
import { Link, useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { GetAllEvents, DeleteEvent } from "../../Backend/ApiCalls";
import { Notification } from "../NotificationLoginComponent";
import { BottomNav } from "../BottomNav";
import { publicationNotificationRoute } from '../../utils/notificationLinks';

// ✅ Lightbox + Zoom (pinch to zoom)
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

const API_URL = "https://api.fallaplazarodrigo.es:999";

// --- Helpers ---
// Normaliza el campo images que viene de la BD (string -> array)
const parseImages = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === "[]") return [];

    // Caso típico en tu BD: ['...'] -> lo pasamos a ["..."]
    let jsonLike = trimmed;
    if (trimmed.startsWith("['") && trimmed.endsWith("']")) {
      jsonLike = trimmed.replace(/'/g, '"');
    }

    try {
      const parsed = JSON.parse(jsonLike);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // Último recurso: separar por comas o tratarlo como una ruta única
      if (trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [trimmed];
    }
  }

  return [];
};

// Normaliza recipients
const parseRecipients = (r) => {
  if (!r) return [];
  if (Array.isArray(r)) return r;
  try {
    return typeof r === "string" ? JSON.parse(r) : [];
  } catch {
    return [];
  }
};

export const NotificacionesComponent = (props) => {
  const [notificaciones, setNotificaciones] = useState([]);
  const { user } = props;
  const [notificationMessage, setNotificationMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [isErrorNoti, setIsErrorNoti] = useState(false);
  const navigate = useNavigate();

  // ✅ Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxSlides, setLightboxSlides] = useState([]);

  const resolveImgUrl = (img) =>
    img?.startsWith("http") ? img : `${API_URL}${img}`;

  const openLightbox = (images, index) => {
    const slides = (images || []).map((src) => ({
      src: resolveImgUrl(src),
    }));
    setLightboxSlides(slides);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await GetAllEvents();
        const sorted = data.sort((a, b) => b.id - a.id);

        // Aquí forzamos que todos los events tengan images como array
        const normalized = sorted.map((ev) => ({
          ...ev,
          images: parseImages(ev.images),
        }));

        setNotificaciones(normalized);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const formatFechaHora = (fecha, hora) => {
    const fechaLocal = new Date(fecha).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const horaLocal = new Date(`2024-01-01T${hora}`).toLocaleTimeString(
      "es-ES",
      { hour: "2-digit", minute: "2-digit" }
    );
    return `${fechaLocal} a las ${horaLocal}`;
  };

  const ageTypeMap = {
    0: "exentos",
    1: "infantiles",
    2: "juveniles",
    3: "adultos",
  };

  const filteredNotificaciones = notificaciones.filter((n) => {
    if (!user) return true; // por si acaso
    const recs = parseRecipients(n.recipients);
    const userId = user?.Id ?? user?.id;
    return (
      recs.includes("todos") ||
      recs.includes(ageTypeMap[user.ageType]) ||
      // Notificaciones dirigidas a personas concretas ("user:<Id>")
      (userId != null && recs.includes(`user:${userId}`))
    );
  });

  const handleDeleteNotification = (id) => {
    confirmAlert({
      title: "Confirmación de eliminación",
      message: "¿Eliminar esta notificación?",
      buttons: [
        {
          label: "Sí",
          onClick: async () => {
            try {
              const { response } = await DeleteEvent(id);
              if (response.ok)
                setNotificaciones((ns) => ns.filter((x) => x.id !== id));
              setNotificationMessage(
                response.ok ? "Notificación eliminada" : "Error al eliminar"
              );
              setIsErrorNoti(!response.ok);
            } catch {
              setNotificationMessage("Error al eliminar");
              setIsErrorNoti(true);
            }
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 5000);
          },
        },
        { label: "No", onClick: () => { } },
      ],
    });
  };

  const formatText = (text) =>
    text
      ?.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br />") || "";

  return (
    <div className="mostrar-notificaciones">
      {showNotification && (
        <Notification
          message={notificationMessage}
          type={isErrorNoti ? "error" : "success"}
        />
      )}

      <BottomNav I18n={props.I18n} user={user} />

      <div className="recipient">
        <h1 className="titulo-page">{props.I18n.Notifications}</h1>

        {filteredNotificaciones.length ? (
          <ul className="notificaciones-list">
            {filteredNotificaciones.map((n, i) => (
              <li key={i} className="notificacion-item">
                <h3>{n.title}</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html: formatText(n.description),
                  }}
                />

                {/* ✅ Imágenes de la notificación (click -> lightbox + zoom) */}
                {n.images && n.images.length > 0 && (
                  <div className="notification-images">
                    {n.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={resolveImgUrl(img)}
                        alt={`Imagen ${idx + 1}`}
                        loading="lazy"
                        className="zoomable-img"
                        onClick={() => openLightbox(n.images, idx)}
                      />
                    ))}
                  </div>
                )}

                <p>
                  <strong>Fecha:</strong> {formatFechaHora(n.fecha, n.hora)}
                </p>
                <p>
                  <strong>Lugar:</strong> {n.location}
                </p>
                <p>
                  <strong>Destinatarios:</strong>{" "}
                  {parseRecipients(n.recipients).join(", ")}
                </p>

                {n.surveyLinked !== 0 && n.selectedSurvey && (
                  <p>
                    <strong>Encuesta vinculada:</strong>{" "}
                    <Link
                      to={`/contestar-encuesta/${n.selectedSurvey}`}
                      className="survey-link-button"
                    >
                      Responder Encuesta
                    </Link>
                  </p>
                )}

                {n.publicationId && (
                  <p>
                    <strong>Publicación vinculada:</strong>{" "}
                    <Link
                      to={publicationNotificationRoute(n.publicationId)}
                      className="survey-link-button"
                    >
                      Más información
                    </Link>
                  </p>
                )}

                {user?.userRank === 3 && (
                  <div className="admin-buttons">
                    <button
                      className="btn-edit"
                      onClick={() => navigate(`/editar-notificacion/${n.id}`)}
                    >
                      Editar
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteNotification(n.id)}
                    >
                      Eliminar
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>No hay notificaciones disponibles</p>
        )}
      </div>

      {/* ✅ Lightbox global (1 instancia) */}
      <Lightbox
        className="fp-lightbox"
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
        on={{ view: ({ index }) => setLightboxIndex(index) }}
        plugins={[Zoom]}
        carousel={{ imageFit: "contain" }}
        controller={{ closeOnBackdropClick: true, closeOnPullDown: true }}
        zoom={{ scrollToZoom: true, pinchZoomV4: true }}
      />
    </div>
  );
};
